import { FC } from "react";

import { Stack } from "@mui/material";
import { FormBackgroundTheme } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { FormStepContainer } from "../FormStepContainer";

import { useTranslate } from "@/common/hooks/useTranslate";
import { BackgroundStyleSelector } from "@/components/LiveEditorElements/BackgroundStyleSelector/BackgroundStyleSelector";
import { BackgroundStyleSelectorSkeleton } from "@/components/LiveEditorElements/BackgroundStyleSelector/BackgroundStyleSelectorSkeleton";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { LiveEditorCustomColorCard } from "@/components/LiveEditorElements/LiveEditorCustomColorCard/LiveEditorCustomColorCard";
import { LiveEditorSelectColorCard } from "@/components/LiveEditorElements/LiveEditorSelectColorCard/LiveEditorSelectColorCard";
import { LiveEditorTab } from "@/components/LiveEditorElements/LiveEditorTab/LiveEditorTab";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton/DashboardSkeleton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

export type StepColorFormData = {
  color: string;
  isDarkMode: boolean;
  backgroundTheme: FormBackgroundTheme | null;
  colorToShowInPreview: string;
};

export type LiveFormEditorStepColorStatelessProps = {
  handleSaveAndNext: () => Promise<void>;
  handleSaveAndBack?: () => Promise<void>;
  form: UseFormReturn<StepColorFormData>;
  trackOnColorChange?: (color: string) => void;
  trackOnCustomColorCancel?: () => void;
  trackOnBackgroundStyleChange?: (theme: FormBackgroundTheme | null, category: string) => void;
  trackOnDarkModeChange?: (isDarkMode: boolean) => void;
  onGradientColorClick?: () => void;
  isDelightfulPagesActive: boolean;
  isDelightfulPagesLoading: boolean;
  isCustomColorTileActive: boolean;
  isCustomColorTileLoading: boolean;
};

export const LiveFormEditorStepColorStateless: FC<LiveFormEditorStepColorStatelessProps> = ({
  form,
  handleSaveAndNext,
  handleSaveAndBack,
  trackOnColorChange,
  trackOnCustomColorCancel,
  trackOnBackgroundStyleChange,
  trackOnDarkModeChange,
  isDelightfulPagesActive,
  isDelightfulPagesLoading,
  isCustomColorTileActive,
  isCustomColorTileLoading,
}: LiveFormEditorStepColorStatelessProps) => {
  const { t } = useTranslate();
  const { watch, setValue } = form;
  const selectedColor = watch("color");

  const handleColorChange = (color: string) => {
    setValue("color", color);
    setValue("colorToShowInPreview", color, { shouldDirty: true });
    trackOnColorChange?.(color);
  };

  const handleColorToShowInPreviewChange = (color: string) => {
    setValue("colorToShowInPreview", color, { shouldDirty: true });
  };

  const modeOptions = [
    {
      value: false,
      label: t("dashboard", "liveFormEditor.stepColor.lightMode"),
      onChange: () => trackOnDarkModeChange?.(false),
    },
    {
      value: true,
      label: t("dashboard", "liveFormEditor.stepColor.darkMode"),
      onChange: () => trackOnDarkModeChange?.(true),
    },
  ];

  const shapeOptions = [
    {
      value: FormBackgroundTheme.Abstract as FormBackgroundTheme | null,
      label: t("dashboard", "liveFormEditor.stepColor.withShapes"),
    },
    { value: null as FormBackgroundTheme | null, label: t("dashboard", "liveFormEditor.stepColor.noShapes") },
  ];

  return (
    <FormStepContainer
      title={t("dashboard", "liveFormEditor.stepColor.title")}
      actionsProps={{
        onBack: handleSaveAndBack,
        onNext: handleSaveAndNext,
      }}
      form={form}
      animatePreview={false}
    >
      <Stack rowGap={3} paddingBottom={{ xs: 2, md: 0 }}>
        {isCustomColorTileLoading ? (
          <DashboardSkeleton variant="rounded" width="100%" height={120} />
        ) : isCustomColorTileActive ? (
          <LiveEditorCustomColorCard
            formColor={selectedColor}
            setFormColor={handleColorChange}
            setColorToShowInPreview={handleColorToShowInPreviewChange}
          />
        ) : (
          <LiveEditorSelectColorCard
            formColor={selectedColor}
            setFormColor={handleColorChange}
            setColorToShowInPreview={handleColorToShowInPreviewChange}
            onCustomColorCancel={trackOnCustomColorCancel}
          />
        )}

        <LiveEditorBlock gap={1.5}>
          <DashboardTypography variant="subtitle1">
            {t("dashboard", "liveFormEditor.stepColor.selectMode")}
          </DashboardTypography>
          <LiveEditorTab<boolean, StepColorFormData> name="isDarkMode" control={form.control} tabs={modeOptions} />
        </LiveEditorBlock>

        {isDelightfulPagesLoading ? (
          <BackgroundStyleSelectorSkeleton />
        ) : isDelightfulPagesActive ? (
          <BackgroundStyleSelector<StepColorFormData>
            name="backgroundTheme"
            control={form.control}
            onThemeChange={trackOnBackgroundStyleChange}
          />
        ) : (
          <LiveEditorBlock gap={1.5}>
            <DashboardTypography variant="subtitle1">
              {t("dashboard", "liveFormEditor.stepColor.selectStyle")}
            </DashboardTypography>
            <LiveEditorTab<FormBackgroundTheme | null, StepColorFormData>
              name="backgroundTheme"
              control={form.control}
              tabs={shapeOptions}
            />
          </LiveEditorBlock>
        )}
      </Stack>
    </FormStepContainer>
  );
};
